import { LIVE_ORIGIN } from "@/lib/site-origin";

/**
 * Gör en absolut URL av en sökväg. SEO-ytor (canonical, hreflang, og:*,
 * JSON-LD) är alltid kanoniska och pekar på live-domänen oavsett vilken
 * adress sidan nås via – samma regel som sitemap.xml och robots.txt.
 */
export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${LIVE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Canonical-länk för en sida.
 * hreflang-alternativen renderas centralt i rotlayouten (src/routes/__root.tsx),
 * så de får inte dubbleras här.
 */
export function seoLinks(path: string): { rel: string; href: string }[] {
  return [{ rel: "canonical", href: absoluteUrl(path) }];
}

/** JSON-LD för en artikel (blogg och kunskapsbank). */
export function articleJsonLd(input: {
  path: string;
  headline: string;
  description: string;
  image?: string | undefined;
  datePublished?: string | null | undefined;
  dateModified?: string | null | undefined;
  authorName?: string | undefined;
  locale?: "sv" | "en" | "es";
  type?: "Article" | "BlogPosting";
}) {
  const url = absoluteUrl(input.path);
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": input.type ?? "Article",
      headline: input.headline,
      description: input.description,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      url,
      inLanguage: input.locale ?? "sv",
      ...(input.image ? { image: [absoluteUrl(input.image)] } : {}),
      ...(input.datePublished ? { datePublished: input.datePublished } : {}),
      ...(input.dateModified ? { dateModified: input.dateModified } : {}),
      author: {
        "@type": input.authorName ? "Person" : "Organization",
        name: input.authorName || "Swedish International School",
      },
      publisher: {
        "@type": "Organization",
        name: "Swedish International School",
        url: LIVE_ORIGIN,
      },
    }),
  };
}

/**
 * Sajtens egna delningsbild (1200x630, versionerad adress) – används när sidan
 * inte har någon egen hjältebild. Aldrig en extern skärmdump.
 */
export const DEFAULT_OG_IMAGE = "/og/share-v1.jpg";

/** og:image + twitter:image. Utan argument används sajtens egna delningsbild. */
export function ogImage(src: string = DEFAULT_OG_IMAGE): { property?: string; name?: string; content: string }[] {
  const url = absoluteUrl(src);
  return [
    { property: "og:image", content: url },
    { name: "twitter:image", content: url },
  ];
}

/** og:locale-värde för ett språk. */
export function ogLocale(locale: "sv" | "en" | "es"): string {
  return locale === "en" ? "en_GB" : locale === "es" ? "es_ES" : "sv_SE";
}


/**
 * Titel som alltid får plats i Googles träfflista (max 60 tecken).
 * Suffixet (varumärke eller sektion) tas bort om helheten blir för lång,
 * och i sista hand kortas titeln vid ett ordslut.
 */
export function clampTitle(base: string, suffix = ""): string {
  const clean = base.trim().replace(/\s+/g, " ");
  const withSuffix = suffix ? `${clean} | ${suffix.trim()}` : clean;
  if (withSuffix.length <= 60) return withSuffix;
  if (clean.length <= 60) return clean;
  const cut = clean.slice(0, 59);
  const at = cut.lastIndexOf(" ");
  return `${(at > 30 ? cut.slice(0, at) : cut).replace(/[\s,–-]+$/, "")}…`;
}

/**
 * Metabeskrivning som håller sig inom 160 tecken. Kortar helst vid ett
 * meningsslut, annars vid ett ordslut.
 */
export function clampDescription(text: string): string {
  const clean = text.trim().replace(/\s+/g, " ");
  if (clean.length <= 160) return clean;
  const window = clean.slice(0, 160);
  const sentence = Math.max(window.lastIndexOf(". "), window.lastIndexOf("! "), window.lastIndexOf("? "));
  if (sentence >= 110) return window.slice(0, sentence + 1);
  const word = window.lastIndexOf(" ");
  return `${window.slice(0, word > 110 ? word : 159).replace(/[\s,;:–-]+$/, "")}…`;
}
